import React, { useMemo, useState } from 'react';

import CheckOutlined from '@ant-design/icons/lib/icons/CheckOutlined';
import CloseOutlined from '@ant-design/icons/lib/icons/CloseOutlined';
import { css } from '@emotion/css';
import { schemas } from '@recurrency/core-api-schema';
import { PurchaseTargetLineStatus, RecurrencyReplenishmentMethod } from '@recurrency/core-api-schema/dist/common/enums';
import {
  AggregatedStockAndQuantityFieldsDTO,
  StockAndQuantityFieldsDTO,
} from '@recurrency/core-api-schema/dist/itemLocations/getItemLocations';
import { Form, Spin } from 'antd';
import { ColumnType } from 'antd/lib/table';
import Title from 'antd/lib/typography/Title';

import { mapAggregatedToNetStockRecord } from 'pages/planning/ReplenishmentPathsPage/utils';
import { ItemDetailsSidePane } from 'pages/purchasing/PurchaseTargetsPage/PurchaseTargetLinesPage/sidePane/ItemDetailsSidePane';
import { TargetLineWOverUnderBy } from 'pages/purchasing/PurchaseTargetsPage/PurchaseTargetLinesPage/sidePane/LocationStockPanelContent';
import { useAggregatedNetstockEnabled } from 'pages/purchasing/PurchaseTargetsPage/PurchaseTargetLinesPage/sidePane/utils';
import {
  calculateOverUnderBy,
  focusedRowClassName,
  getQtyOnDraftTransfers,
  getTransferKey,
} from 'pages/purchasing/PurchaseTargetsPage/PurchaseTargetLinesPage/utils';
import { GenericTargetLineRow, PurchaseTargetLineRow } from 'pages/purchasing/PurchaseTargetsPage/types';

import { Button } from 'components/Button';
import { FlexSpace } from 'components/FlexSpace';
import { ParsedInput } from 'components/Input/ParsedInput';
import { Modal } from 'components/Modal';
import { NetStockPopover } from 'components/recipes/equation/NetStockPopover';
import { StickyHeaderVariant, Table } from 'components/Table';

import { useCoreApi } from 'hooks/useApi';

import { formatNumber } from 'utils/formatting';
import { sortDirections } from 'utils/tables';
import { floatOrDefault } from 'utils/units';

import { PurchasingLineTransfer } from 'types/hash-state';

import { InfoTooltip } from '../Tooltip/InfoTooltip';
import { QtyToOrderPopover } from './equation/QtyToOrderPopover';
import { TableSplitPage } from './TableSplitPage';
import { TargetLineStatusBadge } from './TargetLineStatusBadge';

export interface EditPurchaseLineTransfersModalProps {
  targetLine: PurchaseTargetLineRow;
  allLines: GenericTargetLineRow[];
  onClose: (transfers?: PurchasingLineTransfer[]) => void;
}

interface TransferTargetLine extends TargetLineWOverUnderBy {
  transferDays?: number;
  qtyOnDraftTransfers?: number;
  key?: string;
}

export function EditPurchaseLineTransfersModal({ targetLine, allLines, onClose }: EditPurchaseLineTransfersModalProps) {
  const { itemId, itemUid, userTransfers } = targetLine;

  const [focusedLine, setFocusedLine] = useState<PurchaseTargetLineRow>(targetLine);
  const [transferLines, setTransferLines] = useState<PurchasingLineTransfer[]>(userTransfers || []);
  const [inputErrors, setInputErrors] = useState<Record<string, string>>({});
  const { enabled: isNetstockAgg } = useAggregatedNetstockEnabled();
  const totalQtyToTransfer = useMemo(
    () => transferLines.reduce((totalQty, transfer) => totalQty + transfer.qtyToTransfer, 0),
    [transferLines],
  );

  const transferQtyByLocationId: Record<string, number> = useMemo(
    () =>
      Object.fromEntries(transferLines.map((transfer) => [transfer.transferFromLocationId, transfer.qtyToTransfer])),
    [transferLines],
  );

  const { data: targetLinesData, isLoading: isLoadingTargetLinesData } = useCoreApi(
    schemas.purchasing.getPurchaseTargetLines,
    {
      queryParams: {
        filter: {
          itemIds: [itemId],
        },
      },
    },
  );

  const { data: transferDaysData, isLoading: isLoadingTransferDaysData } = useCoreApi(
    schemas.transferDays.getTransferDays,
    {
      queryParams: {
        toLocationId: targetLine.locationId,
      },
    },
  );

  const { data: replenishmentPathData, isLoading: isLoadingReplenishmentPathData } = useCoreApi(
    schemas.itemLocations.getFullReplenishmentPath,
    {
      queryParams: {
        locationId: targetLine.locationId,
        itemId: itemUid,
        includeStockByLocation: true,
      },
    },
  );

  const transferDaysByLocationId: Map<string, number> = useMemo(
    () =>
      (transferDaysData?.items || [])
        .filter((item) => item.toLocationId === targetLine.locationId)
        .reduce((map, item) => {
          map.set(item.fromLocationId, item.days);
          return map;
        }, new Map<string, number>()),
    [targetLine.locationId, transferDaysData?.items],
  );

  // TODO @IsaacGelman abstract to common function used by Location Stock feature
  const targetLines: TransferTargetLine[] = useMemo(
    () =>
      (targetLinesData?.items || [])
        // filter out location of original target line
        .filter((record) => record.locationId !== targetLine.locationId && record.companyId === targetLine.companyId)
        .map((record) => {
          const qtyOnDraftTransfers = getQtyOnDraftTransfers(record, targetLine, allLines);

          /* For netstock agg tenants the qty available and netstock fields are aggregated, leading incorrect values
             Using getFullReplenishmentPath guarantees non-aggregated qty's which are needed for transfers.
             For non netstock agg tenants, this is a no-op
          */
          const stockQuantityByLocationId = replenishmentPathData?.valuesByLocation ?? {};
          const { qtyAvailable: recordQtyAvailable, netStock: recordNetStock } =
            stockQuantityByLocationId?.[record.locationId]?.quantityFields ?? record;
          const adjustedNetStock = recordNetStock - qtyOnDraftTransfers;
          const adjustedQtyAvailable = recordQtyAvailable - qtyOnDraftTransfers;
          return {
            ...record,
            // Overrides aggregated qty values with per location values
            ...stockQuantityByLocationId[record.locationId]?.quantityFields,
            aggregatedQuantityFields: stockQuantityByLocationId[record.locationId]?.aggregatedQuantityFields,
            quantityFields: stockQuantityByLocationId[record.locationId]?.quantityFields,
            qtyOnDraftTransfers,
            netStock: adjustedNetStock,
            qtyAvailable: adjustedQtyAvailable,
            transferDays: transferDaysByLocationId.get(record.locationId),
            key: getTransferKey(record),
            overUnderBy: calculateOverUnderBy(
              record.replenishmentMethod as RecurrencyReplenishmentMethod,
              adjustedNetStock,
              record.invMax,
              record.invMin,
            ),
          };
        })
        // sort by overUnderBy, then by locationId
        .sort((a, b) =>
          a.overUnderBy - b.overUnderBy === 0
            ? a.locationId.localeCompare(b.locationId)
            : (a.overUnderBy - b.overUnderBy) *
              // if targetLine is overstock, show understocked locations first, otherwise show overstocked locations first
              (targetLine.status === PurchaseTargetLineStatus.Understock ? -1 : 1),
        ),
    [targetLinesData?.items, targetLine, allLines, replenishmentPathData?.valuesByLocation, transferDaysByLocationId],
  );

  function handleSave() {
    onClose(transferLines);
  }

  const handleQtyToTransferChange = (newQty: number, line: TransferTargetLine) => {
    const lineKey = `${line.itemId}-${line.locationId}`;
    let error = inputErrors[lineKey] || '';
    if (newQty > line.qtyAvailable) {
      error = `You cannot transfer more than the available quantity (${line.qtyAvailable}) from location ${line.locationId}.`;
      newQty = line.qtyAvailable;
    } else if (newQty <= line.qtyAvailable) {
      error = '';
    }
    setInputErrors((prev) => ({ ...prev, [lineKey]: error }));
    const newTransferLines = [...transferLines];
    const existingTransferIdx = newTransferLines.findIndex(
      (transfer) => transfer.transferFromLocationId === line.locationId,
    );
    if (existingTransferIdx >= 0) {
      if (newQty > 0) {
        // edit existing qtyToTransfer
        newTransferLines[existingTransferIdx] = { ...newTransferLines[existingTransferIdx], qtyToTransfer: newQty };
      } else {
        // remove transfer line
        newTransferLines.splice(existingTransferIdx, 1);
      }
    } else if (newQty > 0) {
      // add new transfer line
      newTransferLines.push({
        transferFromLocationId: line.locationId,
        transferFromLocationName: line.locationName,
        qtyToTransfer: newQty,
      });
    }
    setTransferLines(newTransferLines);
  };

  const targetLineWithAggregatedQuantityFields = useMemo(() => {
    const stockInfoByLocationId = replenishmentPathData?.valuesByLocation?.[targetLine.locationId];
    return {
      ...targetLine,
      aggregatedQuantityFields: stockInfoByLocationId?.aggregatedQuantityFields
        ? mapAggregatedToNetStockRecord(stockInfoByLocationId.aggregatedQuantityFields)
        : undefined,
      quantityFields: stockInfoByLocationId?.quantityFields,
    };
  }, [targetLine, replenishmentPathData?.valuesByLocation]);

  const tableColumns = ({
    // WARNING: This variable name is  the opposite of what the name implies.
    // it is called from the transferTo table with isTransferFrom = true and vis versa
    isTransferFromTable,
  }: {
    isTransferFromTable: boolean;
  }): ColumnType<
    TransferTargetLine & { quantityFields: StockAndQuantityFieldsDTO } & {
      aggregatedQuantityFields: AggregatedStockAndQuantityFieldsDTO;
    }
  >[] => [
    {
      title: 'Location',
      dataIndex: 'locationId',
      fixed: true,
      render: (_: string, record: TransferTargetLine) => (
        <div>
          <div>{record.locationId}</div>
          <div>{record.locationName}</div>
        </div>
      ),
    },
    {
      title: 'Stockable',
      dataIndex: 'stockable',
      align: 'center' as const,
      render: (value: boolean) => (value ? <CheckOutlined /> : null),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      render: (status: PurchaseTargetLineStatus) => <TargetLineStatusBadge status={status} />,
    },
    {
      title: 'Transfer Days',
      dataIndex: 'transferDays',
      align: 'right' as const,
      width: 100,
      render: (value) => formatNumber(value),
      sorter: isTransferFromTable ? false : (a, b) => (a.transferDays ?? 0) - (b.transferDays ?? 0),
    },
    {
      title: 'Min',
      dataIndex: 'invMin',
      align: 'right' as const,
      render: (value, record) => (
        <>
          {formatNumber(value)}
          {record.replenishmentMethod === RecurrencyReplenishmentMethod.OPOQ ? (
            <InfoTooltip title="This is an order point because this item is using the OP/OQ replenishment method" />
          ) : null}
        </>
      ),
    },
    {
      title: 'Max',
      dataIndex: 'invMax',
      align: 'right' as const,
      render: (value, record) => (
        <>
          {formatNumber(value)}
          {record.replenishmentMethod === RecurrencyReplenishmentMethod.OPOQ ? (
            <InfoTooltip title="This is an order quantitty because this item is using the OP/OQ replenishment method" />
          ) : null}
        </>
      ),
    },
    ...(isNetstockAgg && isTransferFromTable
      ? [
          {
            title: 'Net Stock',
            dataIndex: ['aggregatedQuantityFields', 'aggregatedNetStock'],
            align: 'right' as const,
            render: (
              value: number,
              record: TransferTargetLine & { aggregatedQuantityFields: AggregatedStockAndQuantityFieldsDTO },
            ) => (
              <>
                {formatNumber(record.aggregatedQuantityFields?.aggregatedNetStock)} {record.unitOfMeasure}
                <NetStockPopover
                  record={{
                    ...record,
                    ...(record.aggregatedQuantityFields || {}),
                    aggregatedNetStock: record.netStock,
                  }}
                  isAggregatedNetStock
                />
              </>
            ),
          },
        ]
      : []),
    isNetstockAgg
      ? {
          title: `Location Stock`,
          dataIndex: 'netStock',
          align: 'right' as const,
          sortDirections,
          render: (value: number, record: TransferTargetLine & { quantityFields: StockAndQuantityFieldsDTO }) => (
            <>
              {formatNumber(record.quantityFields?.netStock ?? value)} {record.unitOfMeasure}
              <NetStockPopover
                record={
                  // In case of transfer to table, the needed info may already be aggregated so use the data from rep path
                  { ...record, ...record.quantityFields }
                }
              />
            </>
          ),
        }
      : {
          title: 'Net Stock',
          dataIndex: 'netStock',
          align: 'right' as const,
          sortDirections,
          render: (value: number, record: TransferTargetLine) => (
            <>
              {formatNumber(value)} {record.unitOfMeasure}
              <NetStockPopover record={record} />
            </>
          ),
        },
    {
      title: 'Qty Available',
      dataIndex: 'qtyAvailable',
      align: 'right' as const,
      sorter: isTransferFromTable ? false : (a, b) => a.qtyAvailable - b.qtyAvailable,
      sortDirections,
      render: (value: number, record: TransferTargetLine) => `${formatNumber(value)} ${record.unitOfMeasure || ''}`,
    },
    isTransferFromTable
      ? {
          title: 'Required',
          dataIndex: 'qtyToOrder',
          align: 'right' as const,
          render: (
            value: number,
            record: TransferTargetLine & { aggregatedQuantityFields: AggregatedStockAndQuantityFieldsDTO },
          ) => (
            <div
              className={css`
                display: inline-flex;
              `}
            >
              {formatNumber(value)} {record.unitOfMeasure}
              <QtyToOrderPopover record={record} isAggregatedNetStock={isNetstockAgg} />
            </div>
          ),
        }
      : {
          title: 'Over/Under by',
          dataIndex: 'overUnderBy',
          align: 'right' as const,
          sorter: (a, b) => a.overUnderBy - b.overUnderBy,
          defaultSortOrder: 'descend' as const,
          sortDirections,
          render: (value: number, record: TransferTargetLine) => `${formatNumber(value)} ${record.unitOfMeasure || ''}`,
        },
    {
      title: `Qty To Transfer`,
      dataIndex: 'userQtyToOrder',
      width: '140px',
      sorter: isTransferFromTable
        ? false
        : (a, b) => (transferQtyByLocationId[a.locationId] ?? 0) - (transferQtyByLocationId[b.locationId] ?? 0),
      sortDirections,
      render: (_: number, record: TransferTargetLine) =>
        isTransferFromTable ? (
          <div
            className={css`
              display: flex;
              justify-content: center;
            `}
          >
            {totalQtyToTransfer}
          </div>
        ) : (
          <Form.Item
            validateStatus={inputErrors[`${record.itemId}-${record.locationId}`] ? 'error' : undefined}
            help={inputErrors[`${record.itemId}-${record.locationId}`] || undefined}
            style={{ marginBottom: 0 }}
          >
            <FlexSpace>
              <ParsedInput<number>
                size="small"
                valueParser={(value) => floatOrDefault(value, 0)}
                value={transferQtyByLocationId[record.locationId]}
                onValueChange={(value) => handleQtyToTransferChange(value, record)}
                max={record.qtyAvailable}
                // '' is parsed as 0 so totals update immediately.
                ignoreInputWhileEditing={[]}
              />
              <Button
                size="small"
                disabled={!(transferQtyByLocationId[record.locationId] > 0)}
                onClick={() => handleQtyToTransferChange(0, record)}
              >
                <CloseOutlined />
              </Button>
            </FlexSpace>
          </Form.Item>
        ),
    },
  ];

  return (
    <Modal
      visible
      title={`Transfer Item #${itemId} to Location #${targetLine.locationId}`}
      onCancel={() => onClose()}
      centered
      width={1600}
      footer={
        <>
          <Button key="cancel" onClick={() => onClose()}>
            Cancel
          </Button>
          <Button key="save" type="primary" htmlType="submit" onClick={handleSave}>
            Save
          </Button>
        </>
      }
    >
      <TableSplitPage
        left={
          <div
            className={css`
              display: flex;
              flex-direction: column;
              gap: 20px;

              // We don't need this scroll bar element. It's duplicated and causes layout issues.
              // @see https://recurrency.atlassian.net/browse/PE-5481
              .ant-table-sticky-scroll {
                display: none;
              }
            `}
          >
            <Title level={5}>Transfer To</Title>
            <Table
              stickyHeader
              isLoading={isLoadingTargetLinesData || isLoadingTransferDaysData || isLoadingReplenishmentPathData}
              stickyHeaderVariant={StickyHeaderVariant.Modal}
              columns={tableColumns({ isTransferFromTable: true })}
              data={[targetLineWithAggregatedQuantityFields]}
              rowClassName={(record: PurchaseTargetLineRow) => focusedRowClassName(record, focusedLine)}
              onRow={(record) => ({
                onClick: () => setFocusedLine(record),
              })}
            />
            <Title level={5}>Transfer From</Title>
            {isLoadingTargetLinesData || isLoadingTransferDaysData || isLoadingReplenishmentPathData ? (
              <Spin />
            ) : (
              <Table
                stickyHeader
                stickyHeaderVariant={StickyHeaderVariant.Modal}
                scroll={{ x: true }}
                columns={tableColumns({ isTransferFromTable: false })}
                data={targetLines}
                rowClassName={(record: PurchaseTargetLineRow) => focusedRowClassName(record, focusedLine)}
                onRow={(record) => ({
                  onClick: () => setFocusedLine(record),
                })}
              />
            )}
          </div>
        }
        right={<ItemDetailsSidePane itemInfo={focusedLine} allowMinMaxEdit={false} isInModal />}
      />
    </Modal>
  );
}
